"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
// Framer Motion removed for performance — CSS handles announcement bar animation
import {
  Menu,
  Utensils,
  Calendar,
  CalendarDays,
  X,
  PartyPopper,
  MessageSquare,
  ChevronRight,
  ShoppingBag,
  Star,
  Truck,
  MapPin,
  BriefcaseBusiness,
  Scissors,
  Sparkles,
  UsersRound,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  getPublicChromeForPathname,
  type PublicChromeData,
  type PublicChromeLink,
  type PublicChromeLinkKind,
} from "@/lib/public-chrome";
import { analytics } from "@/lib/analytics";
import {
  isOrderFeatureEnabled,
  resolveGuestFacingOrderHref,
} from "@/lib/features";
import {
  TUSCAN_JULY_3_PRIVATE_BUYOUT_ANNOUNCEMENT,
  isSiteAnnouncementActive,
  type SiteAnnouncement,
} from "@/lib/site-announcement";
import { navLinkTransitionTypes } from "@/lib/nav-transition-types";
import { useBrowserPublicHost } from "@/lib/use-browser-public-host";
import { useBrowserPublicLocationId } from "@/lib/use-browser-public-location-id";
import {
  getPublicPageBrand,
  type PublicPageBrand,
} from "@/lib/public-brand-theme";
// Direct module imports; the "@/components/roma" barrel would pull the Roma
// funnel homepage into the shared public chrome bundle (see public-chrome.tsx).
import { RomaReserveMenu } from "@/components/roma/roma-reserve-menu";
import { RomaOrderCta } from "@/components/roma/roma-order-chooser";
import { useRomaReserveSheet } from "@/components/roma/roma-reserve-sheet-context";
import type { RomaReserveLocation } from "@/components/roma/roma-types";

type NavLink = PublicChromeLink & {
  icon: LucideIcon;
};

function getCtaTrackingProps(
  pathname: string,
  location: string,
  ctaText: string,
  destinationUrl: string
) {
  return {
    location,
    cta_text: ctaText,
    destination_url: destinationUrl,
    page_path: pathname,
  };
}

const navIcons: Record<PublicChromeLinkKind, LucideIcon> = {
  menu: Utensils,
  events: CalendarDays,
  "private-events": PartyPopper,
  "holiday-parties": PartyPopper,
  catering: Truck,
  contact: MessageSquare,
  reservation: Calendar,
  consultation: BriefcaseBusiness,
  appointment: Scissors,
  services: Sparkles,
  people: UsersRound,
  order: ShoppingBag,
  club: Star,
  account: UserRound,
  reviews: Star,
  jobs: MessageSquare,
  directions: MapPin,
  about: MessageSquare,
};

function withNavIcons(links: PublicChromeLink[]): NavLink[] {
  return links.map((link) => ({
    ...link,
    icon: navIcons[link.kind],
  }));
}

type MobileHomeStickyTrackingEvent = "menu" | "order" | "reservation";

const headerOrangeCtaClass =
  "border border-[#f45c24] bg-[#f45c24] text-[#1a1a1a] shadow-none hover:bg-[#d94e1a]";

function navigationAccentClasses(
  chrome: PublicChromeData,
  romaFunnelEnabled?: boolean,
) {
  const publicBrand = getPublicPageBrand(chrome.siteConfig);

  if (publicBrand === "chefs-roma") {
    if (romaFunnelEnabled) {
      return {
        cta: "border border-[#b91c1c] bg-[#b91c1c] text-white shadow-none hover:border-[#cf1f1f] hover:bg-[#cf1f1f]",
        solidIcon:
          "text-[#b91c1c] hover:bg-[#b91c1c]/15 hover:text-[#cf1f1f]",
        mobileStickyCta: "bg-[#b91c1c] text-white hover:bg-[#cf1f1f]",
        mobileStickySecondary:
          "border border-white/14 bg-[#1a1a1a] text-white hover:border-white/22 hover:bg-[#1e1e1e]",
      };
    }

    return {
      cta: "border border-[#f45c24] bg-[#f45c24] text-[#0d0d0d] shadow-none hover:bg-[#d94e1a]",
      solidIcon:
        "text-[#f45c24] hover:bg-[#f45c24]/10 hover:text-[#d94e1a]",
      mobileStickyCta: "bg-[#f45c24] text-[#0d0d0d] hover:bg-[#d94e1a]",
      mobileStickySecondary: "bg-[#f6f6f6] text-[#0d0d0d] hover:bg-[#ebebec]",
    };
  }

  return {
    cta: headerOrangeCtaClass,
    solidIcon:
      "text-[var(--cove-copper)] hover:bg-[var(--cove-copper)]/10 hover:text-[var(--bistro-cta-hover)]",
    mobileStickyCta: headerOrangeCtaClass,
    mobileStickySecondary: "bg-secondary text-foreground hover:bg-accent",
  };
}

function romaReservationWidgetHref(chrome: PublicChromeData): string | null {
  if (chrome.location.slug !== "roma-kitchen-southern-highlands") {
    return null;
  }

  return "/reservations#roma-reservation-widget";
}

function chromePublicOrderHref(chrome: PublicChromeData): string {
  return resolveGuestFacingOrderHref({
    orderingMode: chrome.location.ordering.mode,
    orderUrl: chrome.siteConfig.orderUrl,
    guestOrderCta: chrome.location.guestOrderCta,
  });
}

function isTuscanOrderFirstHeaderPath(
  chrome: PublicChromeData,
  pathname: string,
) {
  return (
    chrome.isDefaultLocation &&
    chrome.location.ordering.mode === "first_party" &&
    pathname !== ""
  );
}

function scrollToRomaReservationWidget(href: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const targetUrl = new URL(href, window.location.href);
  const isCurrentPage =
    targetUrl.origin === window.location.origin &&
    targetUrl.pathname === window.location.pathname;
  const widget = document.getElementById("roma-reservation-widget");

  if (!isCurrentPage || !widget) {
    return false;
  }

  window.history.pushState(null, "", `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
  widget.scrollIntoView({ block: "start", behavior: "smooth" });
  return true;
}

function scheduleNavAnalytics(track: () => void) {
  if (typeof window === "undefined") {
    track();
    return;
  }

  const run = () => {
    try {
      track();
    } catch (error) {
      console.debug("Navigation analytics error:", error);
    }
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 1500 });
    return;
  }

  globalThis.setTimeout(run, 0);
}

function NavBrand({
  showSolidBg,
  pathname,
  chrome,
}: {
  showSolidBg: boolean;
  pathname: string;
  chrome: PublicChromeData;
}) {
  return (
    <Link
      href={chrome.brandHref}
      prefetch={false}
      transitionTypes={navLinkTransitionTypes(chrome.brandHref, pathname)}
      className={cn(
        "group inline-flex items-center rounded-full transition-[transform,opacity] duration-300 hover:scale-[1.03] active:scale-[0.98]",
        showSolidBg ? "opacity-95" : "opacity-100"
      )}
      aria-label={`${chrome.brandLabel} home`}
    >
      <Image
        src={chrome.brandLogoSrc}
        alt={`${chrome.brandLabel} logo`}
        width={56}
        height={56}
        className={cn(
          "h-14 w-14 rounded-full object-cover transition-shadow duration-300 md:h-12 md:w-12",
          showSolidBg
            ? "shadow-[0_12px_28px_-20px_oklch(0.2_0.02_40_/_0.55)]"
            : "shadow-[0_14px_34px_-18px_oklch(0.62_0.22_18_/_0.95)]"
        )}
      />
      <span
        className={cn(
          "ml-3 hidden font-serif text-xl font-normal leading-none tracking-tight transition-colors sm:inline-flex",
          showSolidBg ? "text-foreground" : "text-white"
        )}
      >
        {chrome.brandLabel}
      </span>
      <span className="sr-only">{chrome.brandLabel}</span>
    </Link>
  );
}

function DesktopLinks({
  pathname,
  showSolidBg,
  links,
}: {
  pathname: string;
  showSolidBg: boolean;
  links: PublicChromeLink[];
}) {
  return (
    <div className="flex items-center gap-x-5 xl:gap-x-6">
      {links.map((link) => {
        const isExternal = link.external;
        const linkClasses = cn(
          "relative inline-flex min-h-11 items-center justify-center px-1 text-[15px] font-medium tracking-[0.01em] transition-colors duration-200",
          showSolidBg
            ? pathname === link.href
              ? "text-foreground after:absolute after:bottom-1.5 after:left-1 after:right-1 after:h-px after:bg-[var(--cove-copper)]/55"
              : "text-foreground/78 hover:text-foreground"
            : pathname === link.href
              ? "text-white after:absolute after:bottom-1.5 after:left-1 after:right-1 after:h-px after:bg-white/70"
              : "text-white/75 hover:text-white"
        );

        if (isExternal) {
          return (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener"
              className={linkClasses}
            >
              {link.label}
            </a>
          );
        }

        return (
          <Link
            key={link.href}
            href={link.href}
            prefetch={false}
            transitionTypes={navLinkTransitionTypes(link.href, pathname)}
            className={linkClasses}
            onClick={() => {
              scheduleNavAnalytics(() => {
                const tracking = getCtaTrackingProps(
                  pathname,
                  "desktop_nav",
                  link.label,
                  link.href
                );
                if (link.href === "/menu") {
                  analytics.menuBrowseClicked(tracking);
                }
                if (link.kind === "directions") {
                  analytics.directionsClicked(tracking);
                }
                analytics.ctaClicked(link.label, "desktop_nav", tracking);
              });
            }}
          >
            {link.label}
          </Link>
        );
      })}
    </div>
  );
}

function DesktopCtas({
  showSolidBg,
  pathname,
  chrome,
  romaReserveLocations,
  onOpenRomaReserveSheet,
  romaFunnelEnabled,
}: {
  showSolidBg: boolean;
  pathname: string;
  chrome: PublicChromeData;
  romaReserveLocations?: readonly RomaReserveLocation[];
  onOpenRomaReserveSheet?: () => void;
  romaFunnelEnabled?: boolean;
}) {
  const reservationHref =
    romaReservationWidgetHref(chrome) ?? chrome.siteConfig.reservationUrl;
  const orderHref = chromePublicOrderHref(chrome);
  const accent = navigationAccentClasses(chrome, romaFunnelEnabled);
  const useRomaReserveMenu =
    getPublicPageBrand(chrome.siteConfig) === "chefs-roma" &&
    romaReserveLocations &&
    romaReserveLocations.length > 0;
  const desktopTuscanOrderFirst = isTuscanOrderFirstHeaderPath(chrome, pathname);
  const orderEnabled =
    Boolean(orderHref) &&
    !desktopTuscanOrderFirst &&
    (!chrome.isDefaultLocation || isOrderFeatureEnabled());
  const reservationContent = (
    <>
      <Calendar className="mr-1.5 h-4 w-4" />
      Reserve
    </>
  );
  const orderContent = (
    <>
      <ShoppingBag className="mr-1.5 h-4 w-4" />
      Order
    </>
  );
  const menuOrderContent = (
    <>
      <ShoppingBag className="mr-1.5 h-4 w-4" />
      Order online
    </>
  );

  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center gap-2">
        {desktopTuscanOrderFirst ? (
          <Button
            size="sm"
            className={cn(
              "min-h-10 rounded-xl px-4 text-sm font-medium tracking-[0.01em] transition-[background-color,border-color,transform] duration-200 hover:scale-[1.01]",
              accent.cta,
            )}
            asChild
          >
            {orderHref.startsWith("http") ? (
              <a
                href={orderHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Order online",
                      orderHref
                    );
                    analytics.orderStarted(tracking);
                    analytics.ctaClicked("Order online", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {menuOrderContent}
              </a>
            ) : (
              <Link
                href={orderHref}
                prefetch={false}
                transitionTypes={navLinkTransitionTypes(orderHref, pathname)}
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Order online",
                      orderHref
                    );
                    analytics.orderStarted(tracking);
                    analytics.ctaClicked("Order online", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {menuOrderContent}
              </Link>
            )}
          </Button>
        ) : null}
        {useRomaReserveMenu ? (
          /* Roma Phase 1: location-gated reserve dropdown. Phase 0 dark funnel tokens
             will recolor RomaReserveMenu via [data-roma-funnel="on"] chrome vars. */
          <RomaReserveMenu
            locations={romaReserveLocations}
            buttonClassName={accent.cta}
            onOpenMobileSheet={onOpenRomaReserveSheet}
            onLocationSelect={(slug) => {
              // RomaReserveMenu fires reservation_clicked itself (the header
              // sits outside [data-homepage], so data-track never fired here).
              // Keep only the nav-level engagement event to avoid
              // double-counting the conversion.
              scheduleNavAnalytics(() => {
                const selected = romaReserveLocations?.find(
                  (location) => location.slug === slug,
                );
                const tracking = getCtaTrackingProps(
                  pathname,
                  "desktop_nav_cta",
                  "Reserve",
                  selected?.reserveUrl ?? "/reservations#picker",
                );
                analytics.ctaClicked("Reserve", "desktop_nav_cta", tracking);
              });
            }}
          />
        ) : (
          <Button
            size="sm"
            variant={desktopTuscanOrderFirst ? "ghost" : "default"}
            className={cn(
              desktopTuscanOrderFirst
                ? "min-h-10 rounded-xl px-4 font-semibold transition-transform duration-200 hover:scale-[1.02]"
                : "min-h-10 rounded-xl px-4 text-sm font-medium tracking-[0.01em] transition-[background-color,border-color,transform] duration-200 hover:scale-[1.01]",
              desktopTuscanOrderFirst
                ? showSolidBg
                  ? "hover:bg-accent"
                  : "text-white hover:bg-white/10"
                : accent.cta,
            )}
            asChild
          >
            {reservationHref.startsWith("http") ? (
              <a
                href={reservationHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Reserve",
                      reservationHref
                    );
                    analytics.reservationClicked("desktop_nav_cta", tracking);
                    analytics.ctaClicked("Reserve", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {reservationContent}
              </a>
            ) : (
              <Link
                href={reservationHref}
                prefetch={false}
                transitionTypes={navLinkTransitionTypes(reservationHref, pathname)}
                onClick={(event) => {
                  if (romaReservationWidgetHref(chrome) === reservationHref) {
                    if (scrollToRomaReservationWidget(reservationHref)) {
                      event.preventDefault();
                    }
                  }
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Reserve",
                      reservationHref
                    );
                    analytics.reservationClicked("desktop_nav_cta", tracking);
                    analytics.ctaClicked("Reserve", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {reservationContent}
              </Link>
            )}
          </Button>
        )}
        {orderEnabled && getPublicPageBrand(chrome.siteConfig) === "chefs-roma" ? (
          <RomaOrderCta
            trackingLocation="desktop_nav_cta"
            className={cn(
              "inline-flex h-8 items-center justify-center rounded-lg px-3 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]",
              accent.cta,
            )}
          >
            {orderContent}
          </RomaOrderCta>
        ) : orderEnabled ? (
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "font-semibold transition-transform duration-200 hover:scale-[1.02]",
              showSolidBg
                ? "hover:bg-accent"
                : "text-white hover:bg-white/10"
            )}
            asChild
          >
            {orderHref.startsWith("http") ? (
              <a
                href={orderHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Order",
                      orderHref
                    );
                    analytics.orderStarted(tracking);
                    analytics.ctaClicked("Order", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {orderContent}
              </a>
            ) : (
              <Link
                href={orderHref}
                transitionTypes={navLinkTransitionTypes(orderHref, pathname)}
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_cta",
                      "Order",
                      orderHref
                    );
                    analytics.orderStarted(tracking);
                    analytics.ctaClicked("Order", "desktop_nav_cta", tracking);
                  });
                }}
              >
                {orderContent}
              </Link>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function MobileMenu({
  pathname,
  onNavigate,
  chrome,
  romaFunnelEnabled,
}: {
  pathname: string;
  onNavigate: () => void;
  chrome: PublicChromeData;
  romaFunnelEnabled?: boolean;
}) {
  const orderEnabled =
    !chrome.isDefaultLocation || (isOrderFeatureEnabled() && pathname !== "/menu");
  const actionLinks = withNavIcons(
    orderEnabled
      ? chrome.mobileActionLinks
      : chrome.mobileActionLinks.filter((link) => link.kind !== "order"),
  );
  const exploreLinks = withNavIcons(chrome.mobileExploreLinks);
  const secondaryLinks = withNavIcons(chrome.mobileSecondaryLinks);
  // Same target as the desktop logo. Roma resolves to "/" there; a hand-built
  // /locations/<slug> here linked every page to a canonicalized home duplicate.
  const brandHref = chrome.brandHref;
  const accent = navigationAccentClasses(chrome, romaFunnelEnabled);

  const renderMobileLink = (link: NavLink, section: "action" | "explore" | "secondary") => {
    const Icon = link.icon;
    const isActive = !link.external && pathname === link.href;
    const isReservation = link.kind === "reservation";
    const isOrder = link.kind === "order";
    const isMenu = link.kind === "menu";
    const isProminent = section === "action";
    const isRoma = chrome.location.slug === "roma-kitchen-southern-highlands";
    const isRomaDarkFunnel = isRoma && Boolean(romaFunnelEnabled);
    const linkClasses = cn(
      "flex items-center gap-3 rounded-lg px-3 text-[15px] font-medium transition-colors",
      isRomaDarkFunnel
        ? "active:bg-[#1e1e1e]"
        : isRoma
          ? "active:bg-[#ebebec]"
          : "active:bg-accent",
      isProminent ? "h-12" : "h-11",
      isReservation &&
        (isRomaDarkFunnel
          ? "border border-white/14 bg-[#262626] text-white hover:border-white/24 hover:bg-[#303030]"
          : accent.mobileStickyCta),
      isOrder &&
        (isRomaDarkFunnel
          ? accent.mobileStickyCta
          : isRoma
          ? "bg-[#f45c24] text-[#0d0d0d] hover:bg-[#d94e1a]"
          : "bg-accent text-accent-foreground hover:bg-accent/90"),
      isMenu &&
        !isActive &&
        (isRomaDarkFunnel
          ? accent.mobileStickySecondary
          : isRoma
          ? "bg-[#f6f6f6] text-[#0d0d0d] hover:bg-[#ebebec]"
          : "bg-muted text-foreground hover:bg-accent"),
      !isReservation && !isOrder && isActive
        ? isRomaDarkFunnel
          ? accent.mobileStickySecondary
          : isRoma
          ? "bg-[#f6f6f6] text-[#0d0d0d]"
          : "bg-accent text-accent-foreground"
        : !isReservation && !isOrder && !isMenu
          ? isRomaDarkFunnel
            ? "text-[#d4d4d4] hover:bg-[#1e1e1e] hover:text-white"
            : isRoma
            ? "text-[#5b5b5b] hover:bg-[#ebebec] hover:text-[#0d0d0d]"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          : ""
    );
    const handleClick = () => {
      onNavigate();
      scheduleNavAnalytics(() => {
        const location = `mobile_nav_${section}`;
        const tracking = getCtaTrackingProps(
          pathname,
          location,
          link.label,
          link.href
        );
        if (isMenu) analytics.menuBrowseClicked(tracking);
        if (isReservation) analytics.reservationClicked("mobile_nav", tracking);
        if (isOrder) analytics.orderStarted(tracking);
        if (link.kind === "directions") analytics.directionsClicked(tracking);
        analytics.ctaClicked(link.label, location, tracking);
      });
    };
    const content = (
      <>
        <Icon className="h-5 w-5 shrink-0" />
        <span className="truncate">{link.label}</span>
      </>
    );

    if (isOrder && isRoma) {
      return (
        <RomaOrderCta
          key={`${link.kind}-${link.href}`}
          trackingLocation={`mobile_nav_${section}`}
          className={cn(linkClasses, "w-full")}
          onDestinationSelect={onNavigate}
        >
          {content}
        </RomaOrderCta>
      );
    }

    if (link.external) {
      return (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className={linkClasses}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={link.href}
        href={link.href}
        prefetch={false}
        transitionTypes={navLinkTransitionTypes(link.href, pathname)}
        onClick={handleClick}
        className={linkClasses}
      >
        {content}
      </Link>
    );
  };

  return (
    <SheetContent
      side="left"
      className="w-[85vw] max-w-[320px] p-0 flex flex-col data-[state=open]:ease-out data-[state=open]:duration-150 data-[state=closed]:ease-in data-[state=closed]:duration-100 [&>button]:hidden"
    >
      <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
      <SheetDescription className="sr-only">
        Browse {chrome.siteConfig.name} menu, reservations, ordering, catering, and contact links.
      </SheetDescription>
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 border-b shrink-0">
        <Link
          href={brandHref}
          prefetch={false}
          transitionTypes={navLinkTransitionTypes(brandHref, pathname)}
          onClick={onNavigate}
          className="text-lg font-semibold tracking-tight"
        >
          {chrome.siteConfig.name}
        </Link>
        <div className="flex items-center gap-1">
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="-mr-2 h-11 w-11">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </SheetClose>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        <div className="space-y-2 px-3">
          {actionLinks.map((link) => renderMobileLink(link, "action"))}
        </div>
        <div className="mt-4 space-y-1 border-t px-3 pt-4">
          {exploreLinks.map((link) => renderMobileLink(link, "explore"))}
        </div>
        <div className="mt-4 space-y-1 border-t px-3 pt-4">
          {secondaryLinks.map((link) => renderMobileLink(link, "secondary"))}
          {chrome.accountLink
            ? renderMobileLink(
                withNavIcons([chrome.accountLink])[0]!,
                "secondary",
              )
            : null}
        </div>
      </nav>
    </SheetContent>
  );
}

type MobileHomeAction = {
  href: string;
  label: string;
  icon: LucideIcon;
  location: string;
  trackingEvent: MobileHomeStickyTrackingEvent;
  external?: boolean;
};

function getMobileHomeActionLinks(chrome: PublicChromeData): MobileHomeAction[] {
  if (chrome.isDefaultLocation) {
    // Tuscan's mobile funnel keeps direct ordering first while making the
    // first-party reservation flow available beside it. External default
    // locations use siteConfig.orderUrl; empty external hrefs are omitted.
    const orderHref = chromePublicOrderHref(chrome);
    const actions: MobileHomeAction[] = [];
    if (orderHref) {
      actions.push({
        href: orderHref,
        label: "Order",
        icon: ShoppingBag,
        location: "mobile_home_sticky",
        trackingEvent: "order",
        external: orderHref.startsWith("http"),
      });
    }
    actions.push({
      href: chrome.siteConfig.reservationUrl,
      label: "Reserve",
      icon: Calendar,
      location: "mobile_home_sticky",
      trackingEvent: "reservation",
      external: chrome.siteConfig.reservationUrl.startsWith("http"),
    });
    return actions;
  }

  const menuLink =
    chrome.mobileActionLinks.find((link) => link.kind === "menu") ??
    chrome.mobileActionLinks[0];
  const orderLink =
    chrome.mobileActionLinks.find((link) => link.kind === "order") ??
    chrome.mobileActionLinks.find((link) => link.kind === "reservation") ??
    menuLink;

  return [
    {
      href: menuLink?.href ?? chrome.siteConfig.menuUrl,
      label: "Menu",
      icon: Utensils,
      location: "mobile_home_sticky",
      trackingEvent: "menu",
      external: menuLink?.external,
    },
    {
      href: orderLink?.href ?? chromePublicOrderHref(chrome),
      label: orderLink?.kind === "reservation" ? "Reserve" : "Order",
      icon: orderLink?.kind === "reservation" ? Calendar : ShoppingBag,
      location: "mobile_home_sticky",
      trackingEvent: orderLink?.kind === "reservation" ? "reservation" : "order",
      external: orderLink?.external ?? orderLink?.href.startsWith("http"),
    },
  ];
}

function MobileHomeActionBar({
  pathname,
  chrome,
}: {
  pathname: string;
  chrome: PublicChromeData;
}) {
  const accent = navigationAccentClasses(chrome);
  const actionLinks = getMobileHomeActionLinks(chrome);
  const singleAction = actionLinks.length === 1;
  const [hasPassedQuickStrip, setHasPassedQuickStrip] = useState(false);
  const [footerInView, setFooterInView] = useState(false);
  // Tuscan's order-first bar is visible from load and hides only over the footer.
  // Multi-location bars still wait until the guest has moved past the quick strip.
  const isTuscanOwnerBar = chrome.isDefaultLocation;
  const isVisible = (isTuscanOwnerBar || singleAction || hasPassedQuickStrip) && !footerInView;

  useEffect(() => {
    const boundary = document.getElementById("home-mobile-sticky-boundary");
    const footer = document.querySelector("footer");

    if (!boundary) {
      setHasPassedQuickStrip(true);
    }

    const observers: IntersectionObserver[] = [];

    if (boundary) {
      const boundaryObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          setHasPassedQuickStrip(
            !entry.isIntersecting && entry.boundingClientRect.top < 0,
          );
        },
        { threshold: 0 },
      );

      boundaryObserver.observe(boundary);
      observers.push(boundaryObserver);
    }

    if (footer) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          setFooterInView(entry.isIntersecting);
        },
        { threshold: 0 },
      );

      footerObserver.observe(footer);
      observers.push(footerObserver);
    }

    return () => {
      for (const observer of observers) {
        observer.disconnect();
      }
    };
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement;

    if (isVisible) {
      root.dataset.mobileHomeStickyVisible = "true";
      return () => {
        delete root.dataset.mobileHomeStickyVisible;
      };
    }

    delete root.dataset.mobileHomeStickyVisible;
  }, [isVisible]);

  return (
    <nav
      aria-label="Homepage quick actions"
      aria-hidden={!isVisible}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.5rem_+_env(safe-area-inset-bottom))] motion-reduce:transition-none transition-[transform,opacity] duration-300 ease-out lg:hidden",
        // Single-action bar mirrors the menu page's sticky CTA: a standalone
        // button over a white fade instead of the two-button card.
        singleAction &&
          "pointer-events-none bg-gradient-to-t from-white via-white/95 to-transparent px-4 pt-5",
        isVisible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-full opacity-0",
      )}
    >
      <div
        className={cn(
          singleAction
            ? "mx-auto w-full max-w-md"
            : "mx-auto grid max-w-sm grid-cols-2 gap-2 rounded-2xl border border-[#e3e3e3] bg-white p-2 shadow-[0_18px_44px_-22px_rgb(0_0_0_/_0.28)]",
        )}
      >
        {actionLinks.map((action, index) => {
          const Icon = action.icon;
          const isExternal = action.external ?? action.href.startsWith("http");
          const isPrimaryAction = isTuscanOwnerBar ? index === 0 : singleAction || index === 1;
          const className = cn(
            "flex h-14 min-w-0 items-center justify-center gap-2 text-[15px] font-semibold transition-transform duration-200 active:scale-[0.97]",
            singleAction
              ? "pointer-events-auto w-full rounded-2xl font-black shadow-[0_14px_42px_-22px_rgb(244_92_36_/_0.85)]"
              : "rounded-xl",
            // Single-action bar matches the menu page's sticky CTA exactly
            // (terracotta with a white label), not the marketing accent.
            isPrimaryAction
              ? singleAction
                ? "bg-[var(--roma-terracotta)] text-white hover:bg-[var(--roma-saffron-deep)]"
                : accent.mobileStickyCta
              : accent.mobileStickySecondary
          );
          const handleClick = () => {
            scheduleNavAnalytics(() => {
              const tracking = getCtaTrackingProps(
                pathname,
                action.location,
                action.label,
                action.href
              );
              if (action.trackingEvent === "menu") {
                analytics.menuBrowseClicked(tracking);
              }
              if (action.trackingEvent === "order") {
                analytics.orderStarted(tracking);
              }
              if (action.trackingEvent === "reservation") {
                analytics.reservationClicked("mobile_home_sticky", tracking);
              }
              analytics.ctaClicked(action.label, action.location, tracking);
            });
          };
          const content = (
            <>
              <Icon className="h-5 w-5 shrink-0" />
              <span className="truncate">{action.label}</span>
            </>
          );

          if (
            action.trackingEvent === "order" &&
            getPublicPageBrand(chrome.siteConfig) === "chefs-roma"
          ) {
            return (
              <RomaOrderCta
                key="roma-order-chooser"
                trackingLocation={action.location}
                className={cn(className, "w-full")}
              >
                {content}
              </RomaOrderCta>
            );
          }

          if (isExternal) {
            return (
              <a
                key={action.href}
                href={action.href}
                target="_blank"
                rel="noopener"
                className={className}
                onClick={handleClick}
              >
                {content}
              </a>
            );
          }

          return (
            <Link
              key={action.href}
              href={action.href}
              prefetch={false}
              transitionTypes={navLinkTransitionTypes(action.href, pathname)}
              className={className}
              onClick={handleClick}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function AnnouncementBar({
  isHome,
  announcement,
  pathname,
}: {
  isHome: boolean;
  announcement: SiteAnnouncement | null;
  pathname: string;
}) {
  const hasLink = announcement?.linkUrl && announcement?.linkText;
  const showSolidBg = true;

  void isHome;

  if (!announcement) return null;

  return (
    <div
      role="banner"
      aria-label="Announcement"
      className={cn(
        "w-full border-b transition-colors duration-300 announcement-enter",
        showSolidBg
          ? "border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]"
          : "border-white/10 bg-black/70"
      )}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex min-h-10 items-center justify-center gap-x-1.5 py-1">
          <p
            className={cn(
              "m-0 text-center text-xs leading-tight sm:text-sm sm:leading-snug transition-colors duration-300",
              showSolidBg
                ? "text-[var(--roma-espresso)]/80"
                : "text-white/90"
            )}
          >
            {announcement.text}
          </p>
          {hasLink && (
            <Button
              variant="link"
              size="sm"
              className={cn(
                "inline-flex h-auto min-h-11 shrink-0 items-center px-1 py-1 text-xs font-semibold leading-tight sm:text-sm sm:leading-snug",
                "transition-colors duration-300 focus-visible:ring-[var(--roma-saffron)]/40",
                showSolidBg
                  ? "text-[var(--roma-gold-ink)] hover:text-[var(--roma-saffron-deep)]"
                  : "text-[var(--roma-gold-ink)] hover:text-white"
              )}
              asChild
            >
              <Link
                href={announcement.linkUrl!}
                {...(announcement.linkUrl!.startsWith("/")
                  ? { transitionTypes: navLinkTransitionTypes(announcement.linkUrl!, pathname) }
                  : {})}
              >
                {announcement.linkText}
                <ChevronRight className="ml-0.5 h-3 w-3" aria-hidden="true" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Navigation({
  initialPublicHost,
  initialPublicLocationId,
  pageBrand,
  romaFunnelEnabled,
  draftPreview = false,
  cateringOrderingLive = false,
}: {
  initialPublicHost?: string;
  initialPublicLocationId?: string;
  pageBrand?: PublicPageBrand;
  romaFunnelEnabled?: boolean;
  draftPreview?: boolean;
  /** Server-resolved per request host; adds the "Order catering" entry when live. */
  cateringOrderingLive?: boolean;
} = {}) {
  const pathnameFromRouter = usePathname();
  const browserPublicHost = useBrowserPublicHost(initialPublicHost);
  const browserPublicLocationId = useBrowserPublicLocationId(initialPublicLocationId);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const closeMobileMenu = useCallback(() => setOpen(false), []);
  const reserveSheet = useRomaReserveSheet();
  const openRomaReserveSheet = useCallback(() => {
    closeMobileMenu();
    reserveSheet?.openSheet();
  }, [closeMobileMenu, reserveSheet]);
  const chrome = getPublicChromeForPathname(
    pathnameFromRouter ?? "",
    {
      NEXT_PUBLIC_SITE_URL: browserPublicHost,
      NEXT_PUBLIC_LOCATION_ID: browserPublicLocationId,
    },
    { pageBrand, romaFunnelEnabled, draftPreview, cateringOrderingLive },
  );
  const navAccent = navigationAccentClasses(chrome, romaFunnelEnabled);
  const chromeBrand =
    getPublicPageBrand(chrome.siteConfig);
  const romaReserveLocations = reserveSheet?.locations ?? [];
  const useRomaReserveChrome = romaReserveLocations.length > 0;
  // usePathname() is SSR-safe in the App Router: the server HTML and the first
  // client render both see the real route, so the order-first header logic is
  // correct on first paint (no Reserve→Order flash on the primary CTA).
  const pathname = pathnameFromRouter ?? "";

  // `mounted` still gates the header background swap and the interactive mobile
  // menu below, which depend on client-only state rather than the route.
  useEffect(() => {
    setMounted(true);
  }, []);

  const isHome = pathname === "/";
  const isReservations = pathname === "/reservations";
  const isPrivateEvents = pathname.startsWith("/private-events");
  const hideAnnouncementBar = isReservations || isPrivateEvents;
  const activeAnnouncement =
    chrome.location.slug === "tuscan-cove-southern-highlands" &&
    isSiteAnnouncementActive(TUSCAN_JULY_3_PRIVATE_BUYOUT_ANNOUNCEMENT)
      ? TUSCAN_JULY_3_PRIVATE_BUYOUT_ANNOUNCEMENT
      : null;
  const showSolidBg = true;
  const mobileHeaderMenuLink =
    chrome.mobileActionLinks.find((link) => link.kind === "menu") ??
    chrome.mobileActionLinks[0];
  const mobileHeaderMenuHref = mobileHeaderMenuLink?.href ?? "/menu";
  const mobileHeaderMenuExternal = mobileHeaderMenuLink?.external ?? false;
  // Roma is reserve-first (pill scrolls to its in-page reservation widget).
  // Tuscan keeps the owner's primary conversion goal, online ordering, first.
  const mobileHeaderRomaReserve = chromeBrand === "chefs-roma";
  const mobileHeaderTuscanOrder = isTuscanOrderFirstHeaderPath(chrome, pathname);
  const mobileHeaderOrderFirst = mobileHeaderTuscanOrder;
  const mobileHeaderReserveFirst = mobileHeaderRomaReserve;
  const mobileHeaderCtaLabel = mobileHeaderOrderFirst
    ? "Order"
    : mobileHeaderReserveFirst
      ? "Reserve"
      : "Menu";
  const mobileHeaderCtaHref = mobileHeaderOrderFirst
    ? chromePublicOrderHref(chrome)
    : mobileHeaderRomaReserve
    ? romaReservationWidgetHref(chrome) ?? "/#roma-reservation-widget"
    : mobileHeaderReserveFirst
      ? chrome.siteConfig.reservationUrl
      : mobileHeaderMenuHref;
  const mobileHeaderCtaExternal = mobileHeaderOrderFirst || mobileHeaderReserveFirst
    ? mobileHeaderCtaHref.startsWith("http")
    : mobileHeaderMenuExternal;
  const handleMobileHeaderReserveClick = () => {
    scheduleNavAnalytics(() => {
      const tracking = getCtaTrackingProps(
        pathname,
        "mobile_header",
        mobileHeaderCtaLabel,
        useRomaReserveChrome ? "/reservations#picker" : mobileHeaderCtaHref,
      );
      if (mobileHeaderOrderFirst) {
        analytics.orderStarted(tracking);
        analytics.ctaClicked("Order", "mobile_header", tracking);
      } else if (mobileHeaderRomaReserve || mobileHeaderReserveFirst) {
        analytics.reservationClicked("mobile_header", tracking);
        analytics.ctaClicked("Reserve", "mobile_header", tracking);
      } else {
        analytics.menuBrowseClicked(tracking);
        analytics.ctaClicked("Menu", "mobile_header", tracking);
      }
    });
  };

  const handleMobileHeaderCtaClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (mobileHeaderRomaReserve && scrollToRomaReservationWidget(mobileHeaderCtaHref)) {
      event.preventDefault();
    }

    handleMobileHeaderReserveClick();
  };

  return (
    <>
      <header
        data-public-chrome-brand={chromeBrand}
        className="fixed top-0 left-0 right-0 z-50 [view-transition-name:site-header]"
      >
      {/* Announcement Banner - hidden on reservations and private-events pages */}
      {!hideAnnouncementBar && (
        <AnnouncementBar
          isHome={isHome}
          announcement={activeAnnouncement}
          pathname={pathname}
        />
      )}

      {/* Main Navigation */}
      <div
        className={cn(
          "transition-[background-color,border-color,box-shadow] duration-200",
          // Before mount: use header-initial class (styled by CSS based on data-header-solid attribute)
          // After mount: use React state for dynamic updates
          !mounted && "header-initial",
          mounted && (showSolidBg
            ? romaFunnelEnabled && chromeBrand === "chefs-roma"
              ? "border-b border-white/10 bg-[#0d0d0d]/95 shadow-[0_18px_44px_-30px_rgba(0,0,0,0.75)] backdrop-blur-sm"
              : "border-b border-border/70 bg-background/95 shadow-[var(--shadow-soft)] backdrop-blur-sm"
            : "bg-gradient-to-b from-black/50 to-transparent")
        )}
      >
        <nav className="relative mx-auto flex h-[68px] max-w-7xl items-center px-4 md:h-16 md:px-6">
          <NavBrand showSolidBg={showSolidBg} pathname={pathname} chrome={chrome} />
          <div className="pointer-events-none absolute inset-x-4 hidden items-center justify-center md:inset-x-6 lg:flex">
            <div className="pointer-events-auto">
              <DesktopLinks
                pathname={pathname}
                showSolidBg={showSolidBg}
                links={chrome.desktopNavLinks}
              />
            </div>
          </div>
          <div className="ml-auto hidden shrink-0 items-center gap-2 lg:flex">
            {chrome.accountLink ? (
              <Link
                href={chrome.accountLink.href}
                prefetch={false}
                transitionTypes={navLinkTransitionTypes(chrome.accountLink.href, pathname)}
                aria-label={chrome.accountLink.label}
                title={chrome.accountLink.label}
                className={cn(
                  "inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-200",
                  showSolidBg ? navAccent.solidIcon : "text-white hover:bg-white/10",
                )}
                onClick={() => {
                  scheduleNavAnalytics(() => {
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "desktop_nav_account",
                      chrome.accountLink!.label,
                      chrome.accountLink!.href,
                    );
                    analytics.ctaClicked(
                      chrome.accountLink!.label,
                      "desktop_nav_account",
                      tracking,
                    );
                  });
                }}
              >
                <UserRound className="h-5 w-5" />
                <span className="sr-only">{chrome.accountLink.label}</span>
              </Link>
            ) : null}
            <DesktopCtas
              showSolidBg={showSolidBg}
              pathname={pathname}
              chrome={chrome}
              romaReserveLocations={useRomaReserveChrome ? romaReserveLocations : undefined}
              onOpenRomaReserveSheet={useRomaReserveChrome ? openRomaReserveSheet : undefined}
              romaFunnelEnabled={romaFunnelEnabled}
            />
          </div>

          {/* Mobile menu - render placeholder until mounted to prevent hydration mismatch */}
          {!mounted ? (
            <div className="ml-auto flex items-center gap-2 lg:hidden">
              <Button
                size="sm"
                className={cn(
                  "min-h-11 rounded-xl px-5 text-sm font-semibold",
                  navAccent.cta,
                )}
              >
                {mobileHeaderCtaLabel}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-12 w-12 rounded-full transition-colors duration-300",
                  "active:scale-95",
                  showSolidBg
                    ? navAccent.solidIcon
                    : "text-white hover:bg-white/10"
                )}
              >
                <Menu className="h-7 w-7" />
                <span className="sr-only">Open navigation</span>
              </Button>
            </div>
          ) : (
            <div className="ml-auto flex items-center gap-2 lg:hidden">
              {mobileHeaderRomaReserve && useRomaReserveChrome ? (
                <Button
                  size="sm"
                  type="button"
                  className={cn(
                    "min-h-11 rounded-xl px-5 text-sm font-semibold",
                    navAccent.cta,
                  )}
                  onClick={() => {
                    handleMobileHeaderReserveClick();
                    openRomaReserveSheet();
                  }}
                >
                  {mobileHeaderCtaLabel}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className={cn(
                    "min-h-11 rounded-xl px-5 text-sm font-semibold",
                    navAccent.cta,
                  )}
                  asChild
                >
                  {mobileHeaderCtaExternal ? (
                    <a
                      href={mobileHeaderCtaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleMobileHeaderCtaClick}
                    >
                      {mobileHeaderCtaLabel}
                    </a>
                  ) : (
                    <Link
                      href={mobileHeaderCtaHref}
                      prefetch={false}
                      transitionTypes={navLinkTransitionTypes(mobileHeaderCtaHref, pathname)}
                      onClick={handleMobileHeaderCtaClick}
                    >
                      {mobileHeaderCtaLabel}
                    </Link>
                  )}
                </Button>
              )}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-12 w-12 rounded-full transition-colors duration-300",
                      "active:scale-95",
                      showSolidBg
                        ? navAccent.solidIcon
                        : "text-white hover:bg-white/10"
                    )}
                  >
                    <Menu className="h-7 w-7" />
                    <span className="sr-only">Open navigation</span>
                  </Button>
                </SheetTrigger>
                <MobileMenu
                  pathname={pathname}
                  onNavigate={closeMobileMenu}
                  chrome={chrome}
                  romaFunnelEnabled={romaFunnelEnabled}
                />
              </Sheet>
            </div>
          )}
        </nav>
      </div>
      </header>

      {mounted && isHome && !open && chromeBrand !== "chefs-roma" ? (
        <MobileHomeActionBar pathname={pathname} chrome={chrome} />
      ) : null}

    </>
  );
}
